import type {
  ChatAttachment,
  ChatBackend,
  ChatConfig,
  ChatHistoryEntry,
  ChatMessage,
  EntryType,
  LogEntry,
  Settings,
  UserAccount,
  UserPayload,
  UserRole,
  UserUpdatePayload,
} from './types'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''
const CHAT_API_BASE = (import.meta.env.VITE_CHAT_API_URL ?? '').trim()
const CHAT_ENDPOINT = buildChatEndpoint(CHAT_API_BASE)
const CHAT_CONFIG_ENDPOINT = buildChatConfigEndpoint(CHAT_API_BASE)
const CHAT_TIMEOUT_MS = Math.max(
  1000,
  Number(import.meta.env.VITE_CHAT_TIMEOUT_MS ?? '45000') || 45000
)

export const defaultSettings: Settings = {
  categories: {
    did: ['Exercise', 'Work', 'Study'],
    ate: ['Breakfast', 'Lunch', 'Dinner', 'Snack'],
    drank: ['Water', 'Coffee', 'Tea'],
    other: ['Mood', 'Note']
  }
}

type ApiError = Error & { status?: number }

export interface ChatRequestOptions {
  backend: ChatBackend
  openAiModelId?: string
  bedrockModelId?: string
  mode?: 'chat' | 'think'
  attachments?: ChatAttachment[]
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {}
  if (init?.body && !(init.headers && 'Content-Type' in init.headers)) {
    headers['Content-Type'] = 'application/json'
  }

  const method = init?.method ?? 'GET'
  const requestUrl = `${API_BASE}${path}`
  console.log('SENT:', method, requestUrl, init?.body ?? null)
  const res = await fetch(requestUrl, {
    ...init,
    ...(method === 'GET' && !init?.cache ? { cache: 'no-store' } : {}),
    headers: {
      ...headers,
      ...(init?.headers || {}),
    },
  })
  console.log('RECEIVED:', method, requestUrl, res.status)

  if (!res.ok) {
    const message = await res.text()
    const err = new Error(message || res.statusText) as ApiError
    err.status = res.status
    throw err
  }

  if (res.status === 204) {
    return undefined as T
  }

  try {
    return (await res.json()) as T
  } catch {
    return undefined as T
  }
}

function withUser(path: string, user: string) {
  if (!user) return path
  const joinChar = path.includes('?') ? '&' : '?'
  return `${path}${joinChar}user=${encodeURIComponent(user)}`
}

function withCacheBust(path: string, cacheBust?: string) {
  if (!cacheBust) return path
  const joinChar = path.includes('?') ? '&' : '?'
  return `${path}${joinChar}t=${encodeURIComponent(cacheBust)}`
}

function resolveApiUrl(path?: string) {
  if (!path) return undefined
  if (/^https?:\/\//i.test(path)) {
    return path
  }
  const trimmedBase = API_BASE.replace(/\/+$/, '')
  if (!trimmedBase) {
    return path
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${trimmedBase}${normalizedPath}`
}

export async function fetchSettings(user: string, options: { cacheBust?: string } = {}): Promise<Settings> {
  try {
    return await apiFetch<Settings>(withCacheBust(withUser('/api/settings', user), options.cacheBust))
  } catch (err) {
    const status = (err as ApiError).status
    if (status === 404) {
      return defaultSettings
    }
    throw err
  }
}

export async function persistSettings(user: string, settings: Settings) {
  await apiFetch<void>(withUser('/api/settings', user), {
    method: 'PUT',
    body: JSON.stringify(settings),
  })
}

export async function fetchLogs(user: string, options: { cacheBust?: string } = {}): Promise<LogEntry[]> {
  try {
    return await apiFetch<LogEntry[]>(withCacheBust(withUser('/api/logs', user), options.cacheBust))
  } catch (err) {
    const status = (err as ApiError).status
    if (status === 404) {
      return []
    }
    throw err
  }
}

export async function createLog(
  user: string,
  entry: { type: LogEntry['type']; category: string; notes?: string; quantity?: number; photoData?: string }
) {
  return await apiFetch<LogEntry>(withUser('/api/logs', user), {
    method: 'POST',
    body: JSON.stringify(entry),
  })
}

export async function updateLog(
  user: string,
  id: string,
  updates: { category?: string; quantity?: number; notes?: string; type?: EntryType; performedAt?: string; photoData?: string | null }
) {
  return await apiFetch<LogEntry>(withUser(`/api/logs/${id}`, user), {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

export async function removeLog(user: string, id: string) {
  await apiFetch<void>(withUser(`/api/logs/${id}`, user), {
    method: 'DELETE',
  })
}

export async function clearAllLogs(user: string) {
  await apiFetch<void>(withUser('/api/logs', user), {
    method: 'DELETE',
  })
}

export async function fetchUsersList(): Promise<UserAccount[]> {
  try {
    return await apiFetch<UserAccount[]>('/api/users')
  } catch {
    const fallback = await import('./users.json')
    return fallback.default as UserAccount[]
  }
}

export async function updateUserAccount(username: string, updates: UserUpdatePayload) {
  await apiFetch<void>(`/api/users/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

export async function createUserAccount(user: UserPayload) {
  await apiFetch<void>('/api/users', {
    method: 'POST',
    body: JSON.stringify(user),
  })
}

export async function deleteUserAccount(username: string) {
  await apiFetch<void>(`/api/users/${encodeURIComponent(username)}`, {
    method: 'DELETE',
  })
}

export async function recordUserLogin(username: string) {
  try {
    await updateUserAccount(username, { lastLogin: new Date().toISOString() })
  } catch {
    // Best-effort; ignore errors
  }
}

export async function fetchChatConfig(): Promise<ChatConfig | null> {
  if (!CHAT_CONFIG_ENDPOINT) {
    return null
  }
  const res = await fetch(CHAT_CONFIG_ENDPOINT)
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || 'Failed to load chat configuration')
  }
  try {
    return (await res.json()) as ChatConfig
  } catch {
    throw new Error('Chat configuration response was invalid')
  }
}

export async function sendChatMessage(
  history: ChatMessage[],
  message: string,
  context: { logs?: LogEntry[]; settings?: Settings; profile?: { age?: number; sex?: string } } = {},
  options: ChatRequestOptions = { backend: 'openai', mode: 'chat' }
): Promise<string> {
  if (!CHAT_ENDPOINT) {
    throw new Error('Chat endpoint is not configured.')
  }
  const normalizedMode = options.mode === 'think' ? 'think' : 'chat'
  const selectedBackend: ChatBackend = options.backend === 'bedrock' ? 'bedrock' : 'openai'
  const sanitizedLogs = (context?.logs ?? []).map(log => {
    if (!log || typeof log !== 'object') return log
    const clone: Record<string, unknown> = { ...log }
    if ('photoData' in clone) {
      delete clone.photoData
    }
    const previewUrl = resolveApiUrl((log as LogEntry).photoPreviewUrl)
    const downloadUrl = resolveApiUrl((log as LogEntry).photoDownloadUrl)
    if (previewUrl) {
      clone.photoPreviewUrl = previewUrl
    }
    if (downloadUrl) {
      clone.photoDownloadUrl = downloadUrl
    }
    if (
      (log as { photoData?: string }).photoData ||
      (log as { photoAvailable?: boolean }).photoAvailable ||
      previewUrl ||
      downloadUrl
    ) {
      clone.photoAttached = true
    }
    return clone
  })
  const normalizedAttachments = Array.isArray(options.attachments) ? options.attachments.filter(item => item && item.url) : []
  const payload: Record<string, unknown> = {
    history: history.map(item => ({
      role: item.role,
      content: item.content,
    })),
    message,
    mode: normalizedMode,
    backend: selectedBackend,
    openAiModel: selectedBackend === 'openai' ? options.openAiModelId : undefined,
    bedrockModelId: selectedBackend === 'bedrock' ? options.bedrockModelId : undefined,
    context: {
      logs: sanitizedLogs,
      settings: context?.settings ?? null,
      profile: context?.profile ?? null,
    },
  }
  if (normalizedAttachments.length) {
    payload.attachments = normalizedAttachments.map(item => ({
      id: item.id,
      logId: item.logId,
      url: item.url,
      size: item.size === 'large' ? 'large' : 'small',
      label: item.label ?? '',
    }))
  }
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS)
  let res: Response
  try {
    res = await fetch(CHAT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') {
      throw new Error('Chat request timed out. Please try again.')
    }
    throw err
  } finally {
    clearTimeout(timeout)
  }
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || 'Chat request failed')
  }
  try {
    const data = await res.json()
    return data?.reply ?? ''
  } catch {
    return ''
  }
}

function buildChatEndpoint(root: string) {
  if (!root) return ''
  const trimmed = root.replace(/\/+$/, '')
  if (trimmed.toLowerCase().endsWith('/chat')) {
    return trimmed
  }
  return `${trimmed}/chat`
}

function buildChatConfigEndpoint(root: string) {
  if (!root) return ''
  const trimmed = root.replace(/\/+$/, '')
  if (!trimmed) return ''
  if (trimmed.toLowerCase().endsWith('/chat')) {
    return `${trimmed}/config`
  }
  return `${trimmed}/chat/config`
}

export async function recordChatHistory(user: string, entry: ChatHistoryEntry) {
  try {
    await apiFetch<void>(withUser('/api/chat-history', user), {
      method: 'POST',
      body: JSON.stringify(entry),
    })
  } catch (err) {
    console.warn('Failed to store chat history', err)
  }
}

export async function fetchChatHistory(user: string): Promise<ChatHistoryEntry[]> {
  try {
    return await apiFetch<ChatHistoryEntry[]>(withUser('/api/chat-history', user))
  } catch (err) {
    console.warn('Failed to fetch chat history', err)
    return []
  }
}
